import { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { advancedTier, slimTier } from '../constants/form'
import * as faceapi from 'face-api.js'
import { 
	calculateOrientation
} from '../helpers/livenessDetection'
import { t } from 'i18next'
import useSound from 'use-sound'
import successSound from '../assets/sounds/success.mp3'
import { useMediaQuery } from '@mui/material'
import { getVisibleFormInputs } from '../helpers/base'
import { trimLowerCase } from '../helpers/shared'

/* Hook for initializing the FaceAPI models */
export const useInitLivenessDetection = () => {
	const [modelsLoaded, setModelsLoaded] = useState(false)
	const params  = useParams()
	
	/* Load modals */
	useEffect(() => {
		const loadModels = async () => {
			const modelsUrl = '/models'
			try {
				await Promise.all([
					faceapi.nets.tinyFaceDetector.loadFromUri(modelsUrl),      // Lightweight face detector
					faceapi.nets.faceLandmark68Net.loadFromUri(modelsUrl),     // Face landmark detector; points on the face 
					// faceapi.nets.faceRecognitionNet.loadFromUri(modelsUrl), // For Face Recognition
					faceapi.nets.faceExpressionNet.loadFromUri(modelsUrl),     // For detecting facial expressions
					faceapi.nets.ageGenderNet.loadFromUri(modelsUrl)        // For detecting gender
				])
				setModelsLoaded(true)
			} catch (error) {
				console.error('Error loading face-api models:', error)				
			}
		}

    // * Observer to watch for form changes in the DOM and load models when the relevant
    // * input (file__video) is detected. 
    const observer = new MutationObserver(() => {
      const form = document.getElementsByTagName('form')[0]
      if (form) {
        const visibleInputs = getVisibleFormInputs(form)
        const hasAutoVideo = visibleInputs.filter(input => 
          trimLowerCase(input.name).startsWith('file__video')
        ).length > 0
        if (hasAutoVideo) {
          loadModels()
          observer.disconnect() // Stop observing once the models are loaded
        }
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })

    return () => {
        observer.disconnect() // Cleanup observer on component unmount
    }


	}, [])
}

/* Hook for implementing the Facial Recognition */
export const useFacialRecognition = () => {

  /* Liveness check */
	const [stepIndex, setStepIndex] = useState(0);
	const steps = [
		{
			instruction: `${t("videoRecorder.first")}`, // "turn head right"
			checkCondition: (detection) => calculateOrientation(detection, 0.3)?.right,
      skipRenderInHtml: false
		},
		{
			instruction: `${t("videoRecorder.second")}`, // "Turn head left"
			checkCondition: (detection) => calculateOrientation(detection, undefined, 0.7)?.left,
      skipRenderInHtml: false
		},
		{
			instruction: `${t("videoRecorder.third")}`, // "Center head"
			checkCondition: (detection) => calculateOrientation(detection)?.center,
      skipRenderInHtml: false
		},
		{
			instruction: 'Finalizado', // "Finished"
			// For the 'Finished' step, the condition is always false to prevent automatic advancement
			checkCondition: () => false,
      skipRenderInHtml: true
		}
	];
	const [instruction, setInstruction] = useState(steps[stepIndex]?.instruction);
	const [isIntructionStarted, setIsIntructionStarted] = useState(false)
  const [areInstructionsFinished, setAreInstructionsFinished] = useState(false)
	const [isLivenessCompleted, setIsLivenessCompleted] = useState(false)


	const toastShownForStepRef = useRef(null);


  const [isValid, setIsValid] = useState(false) // 'inside' || 'outside'
  const [validObject, setValidObject] = useState({ // Object to handle the alert when no face is detected
    /* Counter for counting the number of frames without face detected */
    counter: 0,
    alerted: false
  })
  const [detection, setDetection] = useState(null); // State for storing the detection result



  const [headOrientation, setHeadOrientation] = useState('center'); // State for storing head orientation


	/* Expression */
	const [expression, setExpression] = useState(null)
	/* Ages */

	let lastAgeUpdate = Date.now();
	const ageUpdateInterval = 1000;

  const refForTimeOut = useRef(null);
  // Function to reset liveness detection
  const resetLivenessDetection = () => {
    // debugger
    clearTimeout(refForTimeOut.current);
    setStepIndex(0)
    setInstruction(steps[0]?.instruction)
    setIsLivenessCompleted(false)
    setIsIntructionStarted(false)
    setAreInstructionsFinished(false)
    // Opcional: limpiar la referencia después de usarla
  refForTimeOut.current = null;
  }


  // onPlay function to start detection when video plays
	const onPlay = async () => {
    try {
      setStepIndex(0)
      setInstruction(steps[0])
  
      const video = document.getElementById('video-record-rtc');
      const canvas = document.getElementById('canvas-overlay');
      if (!video || !canvas) return;
      const context = canvas.getContext('2d');
      const dims = faceapi.matchDimensions(canvas, video, true);
      async function detect() {
        if (video.paused || video.ended) return; // Stop detection if video is paused or ended
  
        // Run initial detection with full details
        const fullFaceDescription = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 288 }))
          .withFaceLandmarks()
          .withFaceExpressions()
          .withAgeAndGender();

  
        // Clear canvas in each iteration
        context.clearRect(0, 0, canvas.width, canvas.height);
        if (fullFaceDescription) {

          setIsValid(true); // Face is detected and valid
          const resizedResults = faceapi.resizeResults(fullFaceDescription, dims);
  
          setDetection(resizedResults); // Store detection result
          const orientation = calculateOrientation(resizedResults)
  
          // Define thresholds: centered ≈ 0.5
          if (orientation?.right) {
            setHeadOrientation('right'); // user's head is turned to THEIR right
          } else if (orientation?.left) {
            setHeadOrientation('left'); // user's head is turned to THEIR left
          } else {
            setHeadOrientation('center');
          }
  
          // Reset valid object state
          setValidObject({ counter: 0, alerted: false });
  
  
          // Set mood/expression from the expressions object
          const expressions = resizedResults?.expressions;
          if (expressions) {
            const currentExpression = Object.entries(expressions).reduce((max, current) =>
              max[1] > current[1] ? max : current
            );
            setExpression(currentExpression);
          }
  
          // Update and draw age and gender information every ageUpdateInterval ms
          let ageGenderResults = fullFaceDescription;
          if (Object.entries(ageGenderResults).length) {
            const now = Date.now();
            if (now - lastAgeUpdate > ageUpdateInterval) {
              ageGenderResults = await faceapi
                .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 160 }))
                .withAgeAndGender();
              lastAgeUpdate = now;
            }
          }
        } else {
          // Increase counter if no face is detected
          setValidObject((prev) => ({ ...prev, counter: prev.counter + 1 }));
          // context.clearRect(0, 0, canvas.width, canvas.height);
        }
  
        // Continue the detection on next animation frame
        requestAnimationFrame(detect);
      }
  
      // Start detection if camera is shown
      if (video) {
        detect();
      }
  
      // Add event listener for play event to ensure detection starts
      video?.addEventListener('play', () => {
        if (video) {
          detect();
        }
      });
      
    } catch (error) {
      console.error("Error in onPlay function:", error);
    }


	}


  return {
		onPlay,
    detection,
    steps,
    stepIndex,
    setStepIndex,
    isIntructionStarted,
    setIsIntructionStarted,
    isLivenessCompleted,
    setIsLivenessCompleted,
    isValid,
    setIsValid,
    validObject,
    setValidObject,
    setInstruction,
    resetLivenessDetection,
    areInstructionsFinished,
    setAreInstructionsFinished,
    refForTimeOut,
  }
  
}

/* Hook for implementing the Actual Liveness Detection */
export const useLivenessDetection = () => {
  const { 
    onPlay, 
    steps, setStepIndex, setIsLivenessCompleted, setIsValid, setValidObject, 
    isValid, validObject, setIsIntructionStarted, detection, isIntructionStarted, 
    stepIndex, isLivenessCompleted, resetLivenessDetection, areInstructionsFinished, setAreInstructionsFinished, refForTimeOut
  } = useFacialRecognition()

	/* UseEffect for handling validation when no face detected */
  const isMobile = useMediaQuery('(max-width:767px)');
	useEffect(() => {
    let time = isMobile ? 5 : 15
		// console.log(validObject.counter);
		if (validObject.counter > time && !validObject.alerted) {
			setValidObject((prev) => {
				return {
					...prev,
					alerted: true
				}
			})
			setIsValid(false)
			// setMessage(t("livenessDetection.faceNotDetected"), 'error', true);
		}
	}, [validObject.counter]);


  // * Actual Liveness Detection process
  const [playSuccessSound] = useSound(successSound);

  const handleFinish = () => {
    // Make sure to clear any existing timeout
    if (refForTimeOut.current) {
      clearTimeout(refForTimeOut.current);
    }

    // Mark instructions as finished
    setAreInstructionsFinished(true);
    console.info("Liveness check sequence awaiting completion...");

    // Store timeout res
    const timeoutId = setTimeout(() => {
        // This block only executes if liveness detection is not reseted
        setIsIntructionStarted(false);
        setIsLivenessCompleted(true);
        playSuccessSound();
        console.info("Liveness check sequence completed!");
    }, 5000); 

    refForTimeOut.current = timeoutId;
  }


  useEffect(() => {
    /* Ensure there's a detection and it is valid, instruction has started, the instruction index is valid and Liveness Check hasn't completed*/
    if (isValid && detection && isIntructionStarted && (stepIndex < (steps.length - 1)) && !isLivenessCompleted && !areInstructionsFinished) {
      const currentStep = steps[stepIndex];
      let isSatisfied = false;


      // Check if the current step's condition exists and is a function
      if (currentStep && typeof currentStep.checkCondition === 'function') {
        try {
          isSatisfied = currentStep.checkCondition(detection);
        } catch (error) {
          console.error("Error evaluating condition for step:", currentStep.instruction, error);
          isSatisfied = false
        }
      }

      // If condition is met, advance to the next step
      if (isSatisfied) {
        const nextStepIndex = stepIndex + 1;

        if ((nextStepIndex < (steps.length - 1)) && isValid) {
          playSuccessSound();
          setStepIndex(nextStepIndex);
        } else { // COMPLETED !!!
          // This was the last step (excluding 'Finalizado' logic if handled differently)
          handleFinish();
        }
      }

    }

  }, [isValid, detection, isIntructionStarted, stepIndex, isLivenessCompleted, areInstructionsFinished, setAreInstructionsFinished]);




  return {
    onPlay,
    setIsIntructionStarted, 
    steps,
    stepIndex,
    resetLivenessDetection,
    isValid, 
    isLivenessCompleted,
    isIntructionStarted
  }

}
